/**
 * Markdown Preview Visualizer — Tool Wrapper
 *
 * Thin IVisualizerTool implementation that delegates rendering to MarkdownView.
 * Supports inline editing via the standard onEdit(string) contract.
 * Input panel: split editor + live preview. Output panel: preview only.
 */

import React from 'react';

import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import type { ISemanticType } from '@/interfaces/ISemanticType';
import type { IVisualizerTool, VisualizerRenderContext } from '@/interfaces/ITool';

import { MarkdownView } from './MarkdownView';

class MarkdownPreviewVisualizerToolImpl implements IVisualizerTool {
  readonly id = 'markdown-preview-visualizer';
  readonly name = 'Markdown Preview';
  readonly description = 'Live markdown editor with split preview — edit raw markdown and see rendered output side by side';
  readonly version = '2.0.0';
  readonly type = 'visualizer' as const;
  readonly category = 'Markdown';
  readonly editable = true;
  readonly keywords = ['markdown', 'preview', 'render', 'html', 'editor', 'visualizer', 'md', 'markup', 'live preview'];

  readonly supportedInputTypes: ISemanticType[] = [SEMANTIC_TYPES.MARKDOWN, SEMANTIC_TYPES.TEXT];
  readonly outputType = SEMANTIC_TYPES.HTML;

  supportsInput(input: string): boolean {
    return Boolean(input.trim());
  }

  readonly attributes = [
    {
      name: 'breaks',
      type: 'boolean' as const,
      label: 'Line Breaks',
      defaultValue: true,
      description: 'Treat single line breaks as hard breaks',
    },
  ];

  render(context: VisualizerRenderContext): React.ReactElement {
    return (
      <ErrorBoundary>
        <MarkdownView
          data={context.input}
          editMode={context.editMode ?? false}
          onEdit={context.onEdit}
          breaks={context.config.breaks !== false}
        />
      </ErrorBoundary>
    );
  }

  toText(input: string): string {
    return input;
  }
}

export const MarkdownPreviewVisualizerTool = new MarkdownPreviewVisualizerToolImpl();
